import { readConfig } from './config.js';
import { hydrate } from './hydrate.js';
import type { JsonObject, RuntimeConfig } from './types.js';

/**
 * vsscms runtime entry point.
 *
 * Two responsibilities:
 *   1. Visitor mode: fetch content JSON, hydrate the page, done.
 *   2. Edit mode: detect `?edit` in the URL and hand off to the lazy-loaded
 *      admin chunk for click-to-edit editing.
 *
 * Hard rule: nothing in this file may statically import from ./admin/*.
 * The admin must be dynamically imported so it becomes a separate chunk
 * that public visitors never download.
 */

async function boot(): Promise<void> {
  let config: RuntimeConfig;
  try {
    config = readConfig();
  } catch (err) {
    console.error(err);
    return;
  }

  if (isEditMode(config)) {
    // Hand off to the admin bundle. Dynamic import is lazy, so this chunk
    // is only fetched when actually needed.
    try {
      const admin = await import('./admin/index.js');
      await admin.boot(config);
    } catch (err) {
      console.error('[vsscms] Failed to load admin chunk', err);
      emit('vsscms:error', { message: 'Failed to load editor', context: String(err) });
    }
    return;
  }

  // Visitor mode: fetch and hydrate.
  const content = await fetchContent(config);
  if (!content) return;

  try {
    hydrate(content, document);
    emit('vsscms:hydrated', { page: config.page, content });
  } catch (err) {
    console.error('[vsscms] Hydration failed', err);
    emit('vsscms:error', { message: 'Hydration failed', context: String(err) });
  }
}

function isEditMode(config: RuntimeConfig): boolean {
  const params = new URLSearchParams(location.search);
  return params.has(config.editParam);
}

async function fetchContent(config: RuntimeConfig): Promise<JsonObject | null> {
  const url = contentUrl(config);
  try {
    const res = await fetch(url, { cache: 'no-cache' });
    if (res.status === 404) {
      // Missing content file — the page still renders with whatever
      // placeholder content is already in the HTML, so we don't throw.
      // But we warn loudly because on a same-origin fetch this almost
      // always means the JSON file wasn't deployed alongside the site.
      console.warn(
        `[vsscms] ${url} not found (404). Make sure content/${config.page}.json is deployed at the site origin.`
      );
      return null;
    }
    if (!res.ok) {
      console.warn(`[vsscms] Content fetch failed: ${res.status} ${res.statusText}`);
      return null;
    }
    return (await res.json()) as JsonObject;
  } catch (err) {
    console.warn('[vsscms] Content fetch error', err);
    return null;
  }
}

/**
 * Build the URL for a content file.
 *
 * Default: same-origin, root-relative. For a site served at
 * https://janetssweetdelights.com/ with page "index", this produces
 * "/content/index.json", which the browser resolves to
 * https://janetssweetdelights.com/content/index.json.
 *
 * Override: `data-content-base` on the script tag is honored as an
 * explicit prefix (used by the dev rig and any site that needs a
 * CDN/custom host). Trailing slashes are trimmed so the caller
 * doesn't double up.
 */
function contentUrl(config: RuntimeConfig): string {
  const { contentBase, contentDir, page } = config;
  const base = contentBase.replace(/\/+$/, '');
  return `${base}/${contentDir}/${page}.json`;
}

function emit<T>(name: string, detail: T): void {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

// Kick things off as soon as the DOM is ready. The script tag is loaded
// with `defer`, so DOMContentLoaded has either fired or will fire imminently.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot());
} else {
  void boot();
}
